/**
 * ThemeContext.js — Global dark/light theme provider for Sixers Performance app.
 *
 * Usage in any component:
 *   import { useTheme, getT } from "./ThemeContext";
 *   ...
 *   const { isDark } = useTheme();
 *   const T = getT(isDark);   // replaces the static T object
 *
 * The toggle lives in LandingPage header. Preference is stored in localStorage.
 */
import React, { createContext, useContext, useState } from "react";

const ThemeContext = createContext({ isDark: true, toggleTheme: () => {} });

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      const s = localStorage.getItem("sixers_theme");
      return s ? s === "dark" : true; // default: dark
    } catch { return true; }
  });

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      try { localStorage.setItem("sixers_theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// ─── Token sets ───────────────────────────────────────────────────────────────
// Brand colors — same in both themes
const BRAND = {
  blue:       "#034e7b",
  blueDark:   "#02304f",
  yellow:     "#f7ce0b",
  font:       "'Segoe UI', system-ui, sans-serif",
  blur:       "blur(12px)",
  yellowBtn:  { background:"linear-gradient(135deg,#f7ce0b,#e8b800)", color:"#02304f", fontWeight:800, border:"none" },
};

const DARK = {
  ...BRAND,
  isDark:       true,
  colorScheme:  "dark",
  pageBg:       "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)",
  glass:        "rgba(255,255,255,0.06)",
  glassBdr:     "1px solid rgba(255,255,255,0.10)",
  glassSh:      "0 4px 28px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.06)",
  textPrimary:  "rgba(255,255,255,0.92)",
  textSub:      "rgba(255,255,255,0.70)",
  textLabel:    "rgba(255,255,255,0.55)",
  textMuted:    "rgba(255,255,255,0.45)",
  gray:         "rgba(255,255,255,0.45)",
  inputBg:      "rgba(255,255,255,0.07)",
  inputBdr:     "rgba(255,255,255,0.10)",
  inputText:    "rgba(255,255,255,0.88)",
  tabActiveBg:  "linear-gradient(135deg,#f7ce0b,#e8b800)",
  tabActiveClr: "#02304f",
  tabInBg:      "rgba(255,255,255,0.06)",
  tabInClr:     "rgba(255,255,255,0.55)",
  // Status colors - Vivid on dark
  green:      "#34d399",
  red:        "#f87171",
  orange:     "#fbbf24",
  greenBg:    "rgba(52,211,153,0.12)",
  redBg:      "rgba(248,113,113,0.12)",
  orangeBg:   "rgba(251,191,36,0.12)",
  // Chart Tokens
  chartBg:      "linear-gradient(160deg, #060d1f 0%, #0a1832 60%, #071428 100%)",
  chartBdr:     "1px solid rgba(255,255,255,0.08)",
  chartGrid:    "rgba(255,255,255,0.04)",
  chartText:    "rgba(255,255,255,0.38)",
  divider:      "rgba(255,255,255,0.08)",
  dropdownBg:   "rgba(8,20,40,0.96)",
  dropdownBdr:  "1px solid rgba(255,255,255,0.10)",
  dropdownItem: "rgba(255,255,255,0.88)",
};

const LIGHT = {
  ...BRAND,
  isDark:       false,
  colorScheme:  "light",
  pageBg:       "#f0f6ff",
  glass:        "rgba(255,255,255,0.95)",
  glassBdr:     "1px solid rgba(3,78,123,0.18)",
  glassSh:      "0 4px 20px rgba(0,40,100,0.08)",
  textPrimary:  "#02304f",
  textSub:      "#1a3a5a",
  textLabel:    "#3a5a7a",
  textMuted:    "#5a7a9a",
  gray:         "#8899aa",
  inputBg:      "#ffffff",
  inputBdr:     "rgba(3,78,123,0.20)",
  inputText:    "#021d35",
  tabActiveBg:  "#f7ce0b",
  tabActiveClr: "#02304f",
  tabInBg:      "rgba(3,78,123,0.06)",
  tabInClr:     "#3a5a7a",
  // Status colors - Darker for visibility on white
  green:      "#059669", 
  red:        "#dc2626",
  orange:     "#d97706",
  greenBg:    "rgba(5,150,105,0.08)",
  redBg:      "rgba(220,38,38,0.08)",
  orangeBg:   "rgba(217,119,6,0.08)",
  // Chart Tokens
  chartBg:      "#ffffff",
  chartBdr:     "1px solid rgba(3,78,123,0.15)",
  chartGrid:    "rgba(3,78,123,0.08)",
  chartText:    "rgba(3,78,123,0.55)",
  divider:      "rgba(3,78,123,0.12)",
  dropdownBg:   "#ffffff",
  dropdownBdr:  "1px solid rgba(3,78,123,0.15)",
  dropdownItem: "#02304f",
};

/**
 * Returns the full theme token set for the given mode.
 * Call this inside your component function (not at module level) to get reactive tokens.
 *
 * @param {boolean} isDark
 * @returns {object} theme tokens
 */
export function getT(isDark) {
  return isDark ? DARK : LIGHT;
}

/**
 * Glass card style shorthand — use as spread: `style={{ ...glassCard(T) }}`
 */
export function glassCard(T) {
  return {
    background:          T.glass,
    backdropFilter:      T.blur,
    WebkitBackdropFilter:T.blur,
    border:              T.glassBdr,
    borderRadius:        16,
    boxShadow:           T.glassSh,
  };
}
